import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core';
import { Drawer, DrawerProps } from '@avangard/ui/core';

import { TestingAnswers, TestingNavigation, TestingQuestion } from '@modules/testing/moleculas';

import type { TestResultsQuestionEntity } from '@modules/testing/entities';

export type StudentSubjectDetailedResultsDrawerProps = Pick<DrawerProps, 'onClose'> & {
    currentQuestionNumber?: number;
    subjectName?: string;
    questions: TestResultsQuestionEntity[];
    onChange: (number: number) => void;
};

const useDrawerStyles = makeStyles(theme => ({
    root: {
        // Кастомная ширина для этого дровера
        width: '900px!important',

        [theme.breakpoints.down('sm')]: {
            width: '100%!important',
        },
    },
    title: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
    },
}));

const Header = styled.div`
    display: flex;
    justify-content: flex-start;
    margin-bottom: 24px;
`;

const StudentSubjectDetailedResultsDrawer = (
    props: StudentSubjectDetailedResultsDrawerProps,
): React.ReactElement => {
    const { currentQuestionNumber = 0, onClose, onChange, subjectName, questions } = props;

    const { t } = useTranslation(['testing']);

    const drawerClasses = useDrawerStyles();

    const handleChangeQuestion = (type: 'increment' | 'decrement') => () => {
        const nextQuestionNumber =
            type === 'increment' ? currentQuestionNumber + 1 : currentQuestionNumber - 1;

        if (nextQuestionNumber > questions.length) {
            onChange(1);
        } else if (nextQuestionNumber === 0) {
            onChange(questions.length);
        } else {
            onChange(nextQuestionNumber);
        }
    };

    const questionIndex = currentQuestionNumber - 1;
    const currentQuestion = !!questions[questionIndex] ? questions[questionIndex] : undefined;
    const hasMultipleCorrectAnswers = currentQuestion?.hasMultipleCorrectAnswers();
    const userAnswersIds = currentQuestion?.userAnswersIds();

    return (
        <Drawer
            externalClasses={drawerClasses}
            open={!!currentQuestionNumber}
            title={`${t('testing:blocks.titles.answers')} · ${subjectName}`}
            onClose={onClose}
        >
            <Header>
                <TestingNavigation
                    currentIndex={currentQuestionNumber}
                    questionsLength={questions.length}
                    onPreviousClick={handleChangeQuestion('decrement')}
                    onNextClick={handleChangeQuestion('increment')}
                />
            </Header>

            <TestingQuestion
                text={currentQuestion?.text}
                src={currentQuestion?.photos[0]?.origin}
            />

            <TestingAnswers
                disabled={true}
                enableCorrectAnswers={true}
                title={
                    hasMultipleCorrectAnswers
                        ? t('testing:actions.choose_multiple_answers')
                        : t('testing:actions.choose_single_answer')
                }
                answers={currentQuestion?.answers}
                answerIds={userAnswersIds}
            />
        </Drawer>
    );
};

export { StudentSubjectDetailedResultsDrawer };
